import { zodResolver } from '@hookform/resolvers/zod';
import { configMgmtControllerUpdateRequestType } from '@kalba/api-client';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { createFileRoute, redirect } from '@tanstack/react-router';
import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { z } from 'zod';
import { FormField } from '../../../../components/forms/form-field';
import { Button } from '../../../../components/ui/button';
import { Input } from '../../../../components/ui/input';
import { Textarea } from '../../../../components/ui/textarea';
import { usePermissions } from '../../../../hooks/use-permissions';
import { useAuthStore } from '../../../../stores/auth.store';
import { PermitTypeShell } from './components/PermitTypeShell';
import type { PermitTypeDetail } from './components/types';

export const Route = createFileRoute('/config/request-types/$requestTypeId/')({
  beforeLoad: () => {
    const { user, permissions } = useAuthStore.getState();
    if (!user) throw redirect({ to: '/login' });
    if (!permissions.includes('config:view')) throw redirect({ to: '/dashboard' });
  },
  component: PermitTypeGeneralPage,
});

const generalSchema = z.object({
  name: z.string().min(1, 'Name is required'),
  description: z.string(),
  hasInspection: z.boolean(),
  inspectionMandatory: z.boolean(),
  hasPledgeStudy: z.boolean(),
  hasRenewal: z.boolean(),
});

type GeneralFormValues = z.infer<typeof generalSchema>;

function GeneralTab({ permitType }: { permitType: PermitTypeDetail }) {
  const { can } = usePermissions();
  const qc = useQueryClient();
  const canEdit = can('config:edit');

  const [deactivateDialog, setDeactivateDialog] = useState(false);

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<GeneralFormValues>({
    resolver: zodResolver(generalSchema),
    defaultValues: {
      name: permitType.name,
      description: permitType.description ?? '',
      hasInspection: permitType.hasInspection,
      inspectionMandatory: permitType.inspectionMandatory,
      hasPledgeStudy: permitType.hasPledgeStudy,
      hasRenewal: permitType.hasRenewal,
    },
  });

  const saveMutation = useMutation({
    mutationFn: (data: GeneralFormValues) =>
      configMgmtControllerUpdateRequestType({ id: permitType.id, requestBody: data }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['permitType', permitType.id] }),
  });

  const deactivateMutation = useMutation({
    mutationFn: () =>
      configMgmtControllerUpdateRequestType({
        id: permitType.id,
        requestBody: { deactivate: true },
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['permitType', permitType.id] });
      qc.invalidateQueries({ queryKey: ['requestTypes'] });
      setDeactivateDialog(false);
    },
  });

  return (
    <div className="space-y-6">
      <div className="rounded-xl border border-gray-100 bg-white p-6">
        <h2 className="mb-4 text-sm font-semibold text-gray-700">General Settings</h2>
        <form onSubmit={handleSubmit((data) => saveMutation.mutate(data))} className="space-y-4">
          <FormField label="Name" required error={errors.name?.message}>
            <Input {...register('name')} disabled={!canEdit} />
          </FormField>

          <FormField label="Code" hint="Read-only">
            <Input value={permitType.code} disabled className="font-mono" />
          </FormField>

          <FormField label="Description">
            <Textarea rows={3} {...register('description')} disabled={!canEdit} />
          </FormField>

          <div className="grid grid-cols-2 gap-4">
            {(
              [
                ['hasInspection', 'Has Inspection'],
                ['inspectionMandatory', 'Inspection Mandatory'],
                ['hasPledgeStudy', 'Has Pledge & Study'],
                ['hasRenewal', 'Has Renewal'],
              ] as const
            ).map(([field, label]) => (
              <label key={field} className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  className="h-4 w-4 rounded border-gray-300 text-brand-500 focus:ring-brand-500"
                  {...register(field)}
                  disabled={!canEdit}
                />
                <span className="text-sm text-gray-700">{label}</span>
              </label>
            ))}
          </div>

          {canEdit && (
            <div className="flex items-center gap-3">
              <Button type="submit" variant="primary" size="sm" disabled={saveMutation.isPending}>
                Save Changes
              </Button>
              {saveMutation.isError && <span className="text-xs text-error-500">Save failed.</span>}
              {saveMutation.isSuccess && <span className="text-xs text-success-600">Saved.</span>}
            </div>
          )}
        </form>
      </div>

      {canEdit && permitType.status === 'ACTIVE' && (
        <div className="rounded-xl border border-error-100 bg-white p-6">
          <h2 className="mb-1 text-sm font-semibold text-error-700">Danger Zone</h2>
          <p className="mb-4 text-xs text-gray-500">
            Deactivating this permit type prevents new permits of this type from being created.
          </p>
          <Button variant="destructive" size="sm" onClick={() => setDeactivateDialog(true)}>
            Deactivate Permit Type
          </Button>
        </div>
      )}

      {deactivateDialog && (
        <div className="fixed inset-0 z-[100000] flex items-center justify-center bg-foreground/15 backdrop-blur-md">
          <div className="w-full max-w-sm rounded-2xl bg-white p-6 shadow-xl">
            <h3 className="mb-2 text-xs font-semibold text-gray-900">Deactivate Permit Type?</h3>
            <p className="mb-6 text-sm text-gray-600">
              No new permits of this type can be created after deactivation. Existing permits are
              not affected.
            </p>
            <div className="flex justify-end gap-2">
              <Button variant="outline" size="sm" onClick={() => setDeactivateDialog(false)}>
                Cancel
              </Button>
              <Button
                variant="destructive"
                size="sm"
                onClick={() => deactivateMutation.mutate()}
                disabled={deactivateMutation.isPending}
              >
                Deactivate
              </Button>
            </div>
            {deactivateMutation.isError && (
              <p className="mt-2 text-xs text-error-500">Deactivation failed.</p>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

function PermitTypeGeneralPage() {
  const { requestTypeId } = Route.useParams();
  return (
    <PermitTypeShell requestTypeId={requestTypeId}>
      {(pt) => <GeneralTab permitType={pt} />}
    </PermitTypeShell>
  );
}
